import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ArtworkToolsPanel } from './ArtworkToolsPanel';
import baseArtwork from '../content/artwork.json';
import baseSite from '../content/site.json';
import type { ArtworkManifest } from '../lib/artwork';
import {
  isAdminPanelOpen,
  loadAdminToken,
  saveAdminToken,
  setAdminPanelOpen,
  subscribeAdminSession,
} from '../lib/admin-session';
import { getRuntimeContent, hydrateRuntimeContent, setRuntimeContent } from '../lib/content-runtime';
import type { SiteContent } from '../lib/site';

type ChatTurn = { id: string; role: 'you' | 'praxis'; text: string };
type StatusMsg = { id: string; phase: string; message: string };

const STATUS_HOLD_MS = 1800;
const STATUS_FADE_MS = 500;

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export function AdminFloatingPanel() {
  const navigate = useNavigate();
  const [token, setToken] = useState(loadAdminToken);
  const [open, setOpen] = useState(isAdminPanelOpen);
  const [minimized, setMinimized] = useState(false);
  const [tab, setTab] = useState<'chat' | 'artwork'>('chat');
  const [pos, setPos] = useState(() => ({
    x: typeof window !== 'undefined' ? Math.max(12, window.innerWidth - 400) : 24,
    y: 96,
  }));
  const dragRef = useRef<{ ox: number; oy: number; px: number; py: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>([
    {
      id: 'welcome',
      role: 'praxis',
      text: 'Hi Su — browse the site while this window stays open. Ask for a change, then look around to review it live.',
    },
  ]);

  const statusQueueRef = useRef<StatusMsg[]>([]);
  const statusPumping = useRef(false);
  const [currentStatus, setCurrentStatus] = useState<StatusMsg | null>(null);
  const [statusVisible, setStatusVisible] = useState(false);
  const [queueTick, setQueueTick] = useState(0);

  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return subscribeAdminSession(() => {
      setToken(loadAdminToken());
      setOpen(isAdminPanelOpen());
    });
  }, []);

  useEffect(() => {
    if (open && token) void hydrateRuntimeContent();
  }, [open, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, busy]);

  /** Pump status messages one-by-one with fade in/out */
  useEffect(() => {
    if (statusPumping.current) return;
    if (statusQueueRef.current.length === 0) return;

    let cancelled = false;
    statusPumping.current = true;

    (async () => {
      while (!cancelled && statusQueueRef.current.length > 0) {
        const next = statusQueueRef.current.shift();
        if (!next) break;

        setStatusVisible(false);
        await sleep(STATUS_FADE_MS);
        if (cancelled) break;

        setCurrentStatus(next);
        await sleep(40);
        setStatusVisible(true);
        await sleep(STATUS_HOLD_MS);
      }
      statusPumping.current = false;
      if (!cancelled && statusQueueRef.current.length > 0) {
        setQueueTick((t) => t + 1);
      }
    })();

    return () => {
      cancelled = true;
      statusPumping.current = false;
    };
  }, [queueTick]);

  const enqueueStatus = useCallback((phase: string, message: string) => {
    statusQueueRef.current.push({
      id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      phase,
      message,
    });
    setQueueTick((t) => t + 1);
  }, []);

  function logout() {
    saveAdminToken('');
    setAdminPanelOpen(false);
    setToken('');
    setOpen(false);
  }

  function closePanel() {
    setAdminPanelOpen(false);
    setOpen(false);
  }

  function onPointerDown(e: ReactPointerEvent) {
    if ((e.target as HTMLElement).closest('button, a, input, textarea')) return;
    const el = panelRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    dragRef.current = { ox: e.clientX, oy: e.clientY, px: pos.x, py: pos.y };
  }

  function onPointerMove(e: ReactPointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    const w = panelRef.current?.offsetWidth ?? 360;
    const maxX = Math.max(8, window.innerWidth - w - 8);
    const maxY = Math.max(8, window.innerHeight - 64);
    setPos({
      x: Math.min(maxX, Math.max(8, d.px + (e.clientX - d.ox))),
      y: Math.min(maxY, Math.max(8, d.py + (e.clientY - d.oy))),
    });
  }

  function onPointerUp(e: ReactPointerEvent) {
    if (dragRef.current) {
      panelRef.current?.releasePointerCapture(e.pointerId);
      dragRef.current = null;
    }
  }

  async function onSend(e: FormEvent) {
    e.preventDefault();
    const message = input.trim();
    if (!message || busy || !token) return;
    setInput('');
    setBusy(true);
    statusQueueRef.current = [];
    setCurrentStatus(null);
    setStatusVisible(false);
    setTurns((t) => [...t, { id: `you-${Date.now()}`, role: 'you', text: message }]);

    try {
      const live = getRuntimeContent();
      const res = await fetch('/api/admin/assist', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-site-admin-token': token,
          accept: 'text/event-stream',
        },
        body: JSON.stringify({
          message,
          site: live.site ?? (baseSite as SiteContent),
          artwork: live.artwork ?? (baseArtwork as ArtworkManifest),
        }),
      });

      if (res.status === 401) {
        logout();
        setTurns((t) => [
          ...t,
          { id: `err-${Date.now()}`, role: 'praxis', text: 'Please sign in again with your passcode.' },
        ]);
        return;
      }

      if (!res.ok || !res.body) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        const msg = err?.error || 'Something went wrong. Please try again.';
        enqueueStatus('error', msg);
        setTurns((t) => [...t, { id: `err-${Date.now()}`, role: 'praxis', text: msg }]);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalSummary = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() ?? '';
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          let dataRaw = '';
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            if (line.startsWith('data:')) dataRaw += line.slice(5).trim();
          }
          if (!dataRaw) continue;
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(dataRaw) as Record<string, unknown>;
          } catch {
            continue;
          }
          if (event === 'status') {
            enqueueStatus(String(data.phase ?? ''), String(data.message ?? ''));
          }
          if (event === 'done') {
            finalSummary = String(data.summary ?? '');
            if (data.ok && data.changed && data.site && data.artwork) {
              setRuntimeContent({
                site: data.site as SiteContent,
                artwork: data.artwork as ArtworkManifest,
                source: 'live',
                updatedAt: String(data.updatedAt ?? ''),
              });
            }
          }
        }
      }

      if (finalSummary) {
        // Wait for status dock to finish fading through messages
        const wait =
          statusQueueRef.current.length * (STATUS_HOLD_MS + STATUS_FADE_MS) + STATUS_HOLD_MS;
        await sleep(Math.min(wait, 8000));
        setTurns((t) => [
          ...t,
          { id: `praxis-${Date.now()}`, role: 'praxis', text: finalSummary },
        ]);
      }
    } catch {
      enqueueStatus('error', 'Could not finish that request. Check your connection.');
      setTurns((t) => [
        ...t,
        {
          id: `err-${Date.now()}`,
          role: 'praxis',
          text: 'Could not finish that request. Please check your connection and try again.',
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  if (!token || !open) return null;

  return (
    <div
      ref={panelRef}
      className={`admin-float${minimized ? ' is-min' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      role="dialog"
      aria-label="Manage your site"
    >
      <header
        className="admin-float-bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="admin-float-title">
          <span className="admin-float-grip" aria-hidden>
            ⋮⋮
          </span>
          <strong>Manage your site</strong>
        </div>
        <div className="admin-float-bar-actions">
          <button
            type="button"
            className="admin-float-icon"
            onClick={() => setMinimized((m) => !m)}
            aria-label={minimized ? 'Expand' : 'Minimize'}
          >
            {minimized ? '▢' : '–'}
          </button>
          <button type="button" className="admin-float-icon" onClick={closePanel} aria-label="Close">
            ×
          </button>
        </div>
      </header>

      {!minimized ? (
        <>
          <div className="admin-float-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={tab === 'chat' ? 'on' : ''}
              aria-selected={tab === 'chat'}
              onClick={() => setTab('chat')}
            >
              Ask
            </button>
            <button
              type="button"
              role="tab"
              className={tab === 'artwork' ? 'on' : ''}
              aria-selected={tab === 'artwork'}
              onClick={() => setTab('artwork')}
            >
              Artwork
            </button>
          </div>

          <div className="admin-status-dock" aria-live="polite">
            <div className="admin-status-dock-label">Status</div>
            <div
              className={`admin-status-dock-msg${statusVisible && currentStatus ? ' is-in' : ' is-out'}`}
            >
              {currentStatus ? (
                <span data-phase={currentStatus.phase}>{currentStatus.message}</span>
              ) : busy ? (
                <span className="admin-status-idle">Working…</span>
              ) : (
                <span className="admin-status-idle">Ready when you are</span>
              )}
            </div>
          </div>

          {tab === 'chat' ? (
            <div className="admin-float-body">
              <div className="admin-float-chat">
                {turns.map((t) => (
                  <div key={t.id} className={`admin-bubble admin-bubble-${t.role}`}>
                    <span className="admin-bubble-label">{t.role === 'you' ? 'You' : 'Praxis'}</span>
                    <p>{t.text}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form className="admin-float-compose" onSubmit={onSend}>
                <label className="visually-hidden" htmlFor="admin-float-request">
                  What would you like to change?
                </label>
                <textarea
                  id="admin-float-request"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder='e.g. “Change flamingo to $90”'
                  rows={2}
                  disabled={busy}
                />
                <div className="admin-float-compose-row">
                  <button type="submit" className="btn btn-primary" disabled={busy || !input.trim()}>
                    {busy ? 'Working…' : 'Send'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="admin-float-body admin-float-artwork">
              <ArtworkToolsPanel />
            </div>
          )}
        </>
      ) : (
        <p className="admin-float-min-hint">Drag me · expand to keep editing while you browse</p>
      )}
    </div>
  );
}
